import { useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';

import { CommentResponse, UserInfo } from '@/api/data-contracts';
import { formatRole } from '@/features/comments/ui/Comments/commentUtils';
import { useUsersService } from '@/features/users';
import { queryKeys } from '@/react-query/constants';

const platformToUserType = (info: UserInfo | undefined): string | undefined => {
  const platform = info?.platforms?.[0];
  if (!platform) return undefined;
  switch (platform) {
    case 'LOCATION':
      return 'location';
    case 'ADVERTISER':
      return 'advertiser';
    case 'SERVICE_PROVIDER':
    case 'SERVICES':
      return 'service';
    case 'BACKOFFICE':
      return 'backoffice';
    default:
      return undefined;
  }
};

export const useCommentItem = (comment: CommentResponse) => {
  const { getUserDetails } = useUsersService();

  const needsLookup = !!comment.author?.id && !comment.author?.userType;

  const { data: userInfo } = useQuery<UserInfo>(
    [queryKeys.user, comment.author?.id],
    ({ signal }) => getUserDetails(comment.author!.id, signal),
    { enabled: needsLookup },
  );

  const getAuthorName = useCallback((c: CommentResponse): string => {
    if (c.author?.firstName && c.author?.lastName) {
      return `${c.author.firstName} ${c.author.lastName}`;
    }
    return c.author?.email || 'Unknown User';
  }, []);

  const userType = comment.author?.userType?.toLowerCase() ?? platformToUserType(userInfo);
  const authorRole = formatRole(comment.author?.position || userInfo?.position);

  return {
    authorName: getAuthorName(comment),
    initial: getAuthorName(comment).charAt(0).toUpperCase(),
    userType,
    authorRole,
  };
};

export default useCommentItem;
